import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useCallback, useEffect, useState } from 'react';
import i18n from '../../i18n';
import { fDateTime } from '../../utils/formatTime';

/** Enough to recognise the chat you just posted in; not a log to scroll. */
const LIMIT = 20;

/**
 * The chats a bot was spoken to in that no gateway claims.
 *
 * Scoped to one bot and opened from its row, because that is the only way the
 * question is ever asked — "I just added this bot to a chat, what is the id?".
 * A global list would make the operator pick their bot out of everyone's.
 *
 * It exists because Telegram refuses getUpdates while a webhook is registered,
 * which is the normal state once a bot is configured, so there is otherwise no
 * way left to read the numeric chat id a gateway needs.
 */
export const TgUnknownChatsDialog = ({ bot, openDialog, closeDialog, server }) => {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(null);

    const fetchData = useCallback(async () => {
        if (!bot?.id) return;
        setError(null);
        const response = await server.search(
            LIMIT,
            [[{ field: 'tgBot.id', op: '=', value: bot.id }]],
            [{ field: 'lastSeenAt', direction: 'DESC' }]
        );
        if (response.error === 'OK') setRows(response.data || []);
        else setError(response.error);
    }, [bot?.id, server]);

    useEffect(() => {
        if (openDialog) fetchData();
    }, [openDialog, fetchData]);

    const close = () => {
        setRows(null);
        setError(null);
        setCopied(null);
        closeDialog();
    };

    const copy = (chatId) => {
        navigator.clipboard.writeText(String(chatId));
        setCopied(chatId);
    };

    return (
        <Dialog open={Boolean(openDialog)} onClose={close} fullWidth maxWidth="md">
            <DialogTitle>
                {i18n.t('dialogs.tgUnknownChats')}
                {bot?.name ? ` — ${bot.name}` : ''}
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    {i18n.t('text.tgUnknownChatsHint')}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {rows === null && !error && (
                    <Stack spacing={1}>
                        <Skeleton height={32} />
                        <Skeleton height={32} />
                        <Skeleton height={32} />
                    </Stack>
                )}

                {rows !== null && rows.length === 0 && (
                    <Alert severity="info">{i18n.t('text.tgUnknownChatsEmpty')}</Alert>
                )}

                {rows !== null && rows.length > 0 && (
                    // The table scrolls inside the dialog rather than widening it:
                    // lastText is free-form and a long one would otherwise stretch
                    // the whole dialog past the viewport.
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{i18n.t('columns.chatId')}</TableCell>
                                    <TableCell>{i18n.t('columns.chatTitle')}</TableCell>
                                    <TableCell>{i18n.t('columns.chatType')}</TableCell>
                                    <TableCell>{i18n.t('columns.lastText')}</TableCell>
                                    <TableCell align="right">{i18n.t('columns.seenCount')}</TableCell>
                                    <TableCell>{i18n.t('columns.lastSeenAt')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                {/* Monospace: this is a number meant to be
                                                    compared by eye against a gateway's chat_id. */}
                                                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                                                    {row.chatId}
                                                </Box>
                                                <Tooltip
                                                    title={i18n.t(
                                                        copied === row.chatId
                                                            ? 'common.copied'
                                                            : 'common.copy'
                                                    )}
                                                >
                                                    <IconButton size="small" onClick={() => copy(row.chatId)}>
                                                        <ContentCopyIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{row.chatTitle || '—'}</TableCell>
                                        <TableCell>{row.chatType || '—'}</TableCell>
                                        <TableCell
                                            sx={{
                                                maxWidth: 280,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            title={row.lastText || ''}
                                        >
                                            {row.lastText || '—'}
                                        </TableCell>
                                        <TableCell align="right">{row.seenCount}</TableCell>
                                        <TableCell>
                                            {row.lastSeenAt ? fDateTime(row.lastSeenAt, 'd MMM yyyy HH:mm') : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                {/* Chats arrive while the dialog is open — post a message, refresh. */}
                <Button startIcon={<RefreshIcon />} onClick={fetchData}>
                    {i18n.t('common.refresh')}
                </Button>
                <Button color="inherit" onClick={close}>{i18n.t('common.close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default TgUnknownChatsDialog;
